// Сборка маршрутов React Router из единого описания разделов
// (shared/constants/routes.ts): страницы грузятся лениво, гард ролей тот же,
// что и в меню.

import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { Center, Loader } from '@mantine/core';
import RoleRoute from '../components/auth/RoleRoute';
import RouteErrorBoundary from '../components/common/RouteErrorBoundary';
import type { RoleName } from 'shared/types/auth';
import { appSections, sectionRoles, type AppSection } from 'shared/constants/routes';

const PageFallback = () => (
  <Center h="60vh">
    <Loader size="lg" />
  </Center>
);

function pageElement(section: AppSection): ReactNode {
  const Page = lazy(section.load as () => Promise<{ default: ComponentType<Record<string, unknown>> }>);
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageFallback />}>
        <Page {...(section.props ?? {})} />
      </Suspense>
    </RouteErrorBoundary>
  );
}

export interface RouteObject {
  path?: string;
  element?: ReactNode;
  children?: RouteObject[];
}

/** Маршруты вне ProtectedLayout. */
export function buildPublicRoutes(sections: AppSection[] = appSections): RouteObject[] {
  return sections
    .filter((section) => section.public)
    .map((section) => ({ path: section.path, element: pageElement(section) }));
}

/**
 * Маршруты под ProtectedLayout: открытые идут как есть, остальные — группами
 * под RoleRoute с теми же ролями, что и в меню.
 */
export function buildProtectedRoutes(sections: AppSection[] = appSections): RouteObject[] {
  const open: RouteObject[] = [];
  const byRoles = new Map<string, { roles: RoleName[]; children: RouteObject[] }>();

  for (const section of sections) {
    if (section.public) continue;
    const route = { path: section.path, element: pageElement(section) };
    const roles = sectionRoles(section);

    if (!roles?.length) {
      open.push(route);
      continue;
    }
    const key = [...roles].sort().join(',');
    const bucket = byRoles.get(key) ?? { roles, children: [] };
    bucket.children.push(route);
    byRoles.set(key, bucket);
  }

  return [
    ...open,
    ...Array.from(byRoles.values()).map(({ roles, children }) => ({
      element: <RoleRoute roles={roles} />,
      children,
    })),
  ];
}
